import * as React from 'react'
import { ArrowLeft, ArrowRight, Crosshair, Trash2 } from 'lucide-react'
import { uploadService } from '@/services/UploadService'
import { fileToResizedDataUrl } from '@/lib/image'
import { requestErrorMessage } from '@/helpers/apiClient'
import { MAX_SPACE_PHOTOS, MAX_SPACE_PHOTO_CAPTION } from '@lib/input/space-photo.input'
import { objectPositionOf, type FocalPoint } from '@lib/helpers/focal-point'
import type { SpacePhotoDto } from '@lib/dto/venue.dto'
import { ImageDropzone } from '@/components/ImageDropzone'
import { FocalPointPicker } from '@/components/FocalPointPicker'
import { Input } from '@/components/ui/input'
import { Tooltip } from '@/components/ui/tooltip'
import { FormError } from '@/components/ui/form-error'

/**
 * The longest side a space photograph is stored at.
 *
 * Matches the cover field: a room photograph is looked at, sometimes full width,
 * so a thumbnail-sized store would be visibly soft — while a photo straight off a
 * phone is several megabytes and would not fit the upload limit.
 */
const PHOTO_MAX_PX = 1600

/**
 * A caption typed locally and saved when the field is left.
 *
 * The photograph's caption is the stored one. Writing it on every keystroke
 * sends a request per character, and the next render puts the stored caption
 * back, so letters disappear. The draft lives here, keyed by the photograph
 * this row is mounted for, and is not copied back from the stored caption
 * while it is open — a save that is still in flight would wipe what was typed
 * since.
 */
function PhotoCaptionInput({
  photoId,
  caption,
  index,
  onCaption,
}: {
  photoId: string
  caption: string | null
  index: number
  onCaption: (photoId: string, caption: string | null) => void
}): React.ReactElement {
  const [draft, setDraft] = React.useState(caption ?? '')

  function commit(): void {
    const next = draft.trim() === '' ? null : draft
    if (next === caption) return
    onCaption(photoId, next)
  }

  return (
    <Input
      value={draft}
      maxLength={MAX_SPACE_PHOTO_CAPTION}
      placeholder="Describe it — also read aloud"
      aria-label={`Description for photograph ${String(index + 1)}`}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key !== 'Enter') return
        e.preventDefault()
        e.currentTarget.blur()
      }}
      className="h-8 text-xs"
    />
  )
}

interface Props {
  photos: readonly SpacePhotoDto[]
  /** False for a caller who may look but not change — the controls disappear. */
  canEdit: boolean
  busy: boolean
  /**
   * Add one photograph. Return the request when the caller has one: each is
   * then finished before the next starts, so the answers can't arrive out of
   * order and the gallery can't step backwards.
   */
  onAdd: (image: string) => void | Promise<void>
  onCaption: (photoId: string, caption: string | null) => void
  onFocalPoint: (photoId: string, focal: FocalPoint | null) => void
  onRemove: (photoId: string) => void
  onReorder: (photoIds: string[]) => void
  /** How many photographs it holds. A room's twelve unless said otherwise (a vendor's gallery). */
  max?: number
  /** What an empty gallery says. */
  emptyText?: string
  /** Who holds them, for the cap message: "A space holds 12 photographs". */
  holder?: string
}

/**
 * A room's photographs: add, describe, order, remove.
 *
 * Deliberately not the shared `CoverPhotoField`. That one owns a single 3:1 banner
 * and is right for the hero of a venue or an event; a room has several
 * photographs, they are ordinary 4:3 photographs rather than banners, and their
 * order carries meaning — the first is the one shown wherever a single image is
 * needed. A component that did both would be two components sharing a name.
 *
 * Ordering is arrows rather than drag-and-drop: with a cap of twelve there is
 * never far to move anything, arrows work from a keyboard without any extra
 * thought, and drag-and-drop on a grid of images is a great deal of machinery for
 * "make this one first".
 */
export function SpacePhotosField({
  photos,
  canEdit,
  busy,
  onAdd,
  onCaption,
  onFocalPoint,
  onRemove,
  onReorder,
  max = MAX_SPACE_PHOTOS,
  emptyText = 'These are shown to clients looking at their event, so they are how somebody knows what this room actually looks like.',
  holder = 'A space',
}: Props): React.ReactElement {
  const [uploading, setUploading] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const [focusing, setFocusing] = React.useState<string | null>(null)
  const full = photos.length >= max
  const room = max - photos.length
  // Read back from `photos` rather than held in state, so the picker shows the
  // saved point after a round trip instead of a stale copy of it.
  const focused = photos.find((p) => p.id === focusing) ?? null

  /**
   * Upload each file in turn, stopping at the cap.
   *
   * In turn rather than all at once: the cap is checked by the server per request,
   * so firing twelve in parallel would race and some would be refused after the
   * others had landed. Sequential is slower and always ends up where the person
   * expects.
   */
  async function onFiles(files: File[]): Promise<void> {
    setError(null)
    setUploading(true)
    try {
      for (const file of files.slice(0, room)) {
        const dataUrl = await fileToResizedDataUrl(file, PHOTO_MAX_PX)
        await onAdd(await uploadService.storeImage(dataUrl))
      }
      if (files.length > room) {
        setError(
          `Added ${String(room)}. ${holder} holds ${String(max)} photographs, so the rest were not.`,
        )
      }
    } catch (e) {
      setError(requestErrorMessage(e))
    } finally {
      setUploading(false)
    }
  }

  function move(index: number, by: number): void {
    const next = photos.map((p) => p.id)
    const target = index + by
    const a = next[index]
    const b = next[target]
    if (a === undefined || b === undefined) return
    next[index] = b
    next[target] = a
    onReorder(next)
  }

  return (
    <div className="space-y-3">
      {photos.length === 0 ? (
        <p className="text-sm text-ink-muted">{emptyText}</p>
      ) : (
        <ul className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {photos.map((photo, index) => (
            <li key={photo.id} className="space-y-1.5">
              <div className="relative overflow-hidden rounded-md border border-line bg-surface-sunken">
                <img
                  src={photo.url}
                  alt={photo.caption ?? ''}
                  className="aspect-[4/3] w-full object-cover"
                  style={{ objectPosition: objectPositionOf(photo.focalPoint) }}
                  loading="lazy"
                />
                {index === 0 ? (
                  <span className="absolute left-1.5 top-1.5 rounded bg-surface/90 px-1.5 py-0.5 text-tiny font-medium uppercase tracking-wide text-ink">
                    Shown first
                  </span>
                ) : null}
              </div>
              {canEdit ? (
                <>
                  <PhotoCaptionInput
                    photoId={photo.id}
                    caption={photo.caption}
                    index={index}
                    onCaption={onCaption}
                  />
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      aria-label={`Move photograph ${String(index + 1)} earlier`}
                      disabled={index === 0 || busy}
                      onClick={() => move(index, -1)}
                      className="grid h-7 w-7 place-items-center rounded text-ink-muted transition-colors hover:bg-surface-hover hover:text-ink disabled:opacity-40"
                    >
                      <ArrowLeft className="h-3.5 w-3.5" />
                    </button>
                    <button
                      type="button"
                      aria-label={`Move photograph ${String(index + 1)} later`}
                      disabled={index === photos.length - 1 || busy}
                      onClick={() => move(index, 1)}
                      className="grid h-7 w-7 place-items-center rounded text-ink-muted transition-colors hover:bg-surface-hover hover:text-ink disabled:opacity-40"
                    >
                      <ArrowRight className="h-3.5 w-3.5" />
                    </button>
                    <Tooltip content="Point at the subject, so every crop keeps it">
                      <button
                        type="button"
                        aria-label={`Set the focal point of photograph ${String(index + 1)}`}
                        disabled={busy}
                        onClick={() => setFocusing(focusing === photo.id ? null : photo.id)}
                        className={`grid h-7 w-7 place-items-center rounded transition-colors disabled:opacity-40 ${
                          focusing === photo.id
                            ? 'bg-accent-soft text-accent-ink'
                            : 'text-ink-muted hover:bg-surface-hover hover:text-ink'
                        }`}
                      >
                        <Crosshair className="h-3.5 w-3.5" />
                      </button>
                    </Tooltip>
                    <button
                      type="button"
                      aria-label={`Remove photograph ${String(index + 1)}`}
                      disabled={busy}
                      onClick={() => onRemove(photo.id)}
                      className="ml-auto grid h-7 w-7 place-items-center rounded text-ink-muted transition-colors hover:bg-surface-hover hover:text-danger-ink disabled:opacity-40"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </>
              ) : photo.caption ? (
                <p className="text-xs text-ink-muted">{photo.caption}</p>
              ) : null}
            </li>
          ))}
        </ul>
      )}

      {focused ? (
        <div className="space-y-2 rounded-md border border-line bg-surface-sunken p-3">
          <p className="text-sm font-medium">Focal point</p>
          <FocalPointPicker
            url={focused.url}
            alt={focused.caption ?? 'this photograph'}
            focalPoint={focused.focalPoint}
            onChange={(focal) => onFocalPoint(focused.id, focal)}
            disabled={busy}
          />
        </div>
      ) : null}

      {canEdit ? (
        <ImageDropzone
          onFiles={(files) => void onFiles(files)}
          multiple
          busy={uploading}
          disabled={uploading || busy || full}
          label={photos.length === 0 ? 'Drag photographs here' : 'Drag more here'}
          hint={
            full
              ? `That is all ${String(max)}. Remove one to add another.`
              : `${String(photos.length)} of ${String(max)} — or click to choose files`
          }
        />
      ) : null}
      {error ? <FormError>{error}</FormError> : null}
    </div>
  )
}
